import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { Loader2, MailCheck, MessageCircle } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/support-contact").then((r) => setPhone(r.data.contact_phone || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setDevToken(data.reset_token || null);
    } catch (err) {
      setError(apiErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const waNumber = phone.replace(/[^\d]/g, "");
  const waText = encodeURIComponent(
    `Hello Certalyth Academy, I forgot my password. My account email is: ${email}. Please help me reset it.`
  );
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : null;

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="forgot-page">
      <div className="w-full max-w-md glass rounded-2xl p-8">
        <div className="flex justify-center mb-6"><Logo className="h-12" showText /></div>
        <h1 className="font-display text-2xl font-bold text-white text-center">Reset password</h1>
        {sent ? (
          <div className="mt-6 text-center">
            <MailCheck className="h-10 w-10 text-gold mx-auto mb-3" />
            <p className="text-slate-300 text-sm">
              Our team has been notified. An administrator will send you a temporary password shortly.
              You will be asked to set a new password the first time you sign in.
            </p>
            <p className="text-slate-400 text-xs mt-3">
              To speed things up, message us on WhatsApp with your account email.
            </p>
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="forgot-whatsapp-link">
                <Button className="w-full mt-4 bg-[#25D366] hover:bg-[#1eb457] text-white h-11" data-testid="forgot-whatsapp-button">
                  <MessageCircle className="h-4 w-4 mr-2" /> Contact admin on WhatsApp
                </Button>
              </a>
            )}
            {devToken && (
              <div className="mt-5 bg-surface-2 rounded-lg p-3 text-xs text-slate-400 break-all">
                <p className="mb-2 text-slate-300">Self-service reset link (optional backup):</p>
                <Link to={`/reset-password?token=${devToken}`} className="text-gold underline" data-testid="reset-dev-link">
                  /reset-password?token={devToken.slice(0, 24)}…
                </Link>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <p className="text-sm text-slate-400 text-center">
              Enter your account email. Our team will send you a temporary password.
            </p>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input"
                className="bg-surface-2 border-white/10 text-white mt-1" />
            </div>
            {error && <p className="text-sm text-rose-400">{error}</p>}
            <Button type="submit" className="w-full btn-gold h-11" disabled={loading} data-testid="forgot-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Request password reset
            </Button>
          </form>
        )}
        <p className="text-sm text-slate-400 text-center mt-6">
          <Link to="/login" className="text-gold hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
